import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import HeroPromptInput from "@/components/HeroPromptInput";
import StudyPanel from "@/components/study/StudyPanel";
import Seo, { localBusinessLd, breadcrumbLd } from "@/lib/seo";
import api from "@/lib/api";


export default function EstimationPage() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      api.get(`/projects/${projectId}`).then(({ data }) => {
        setProject(data);
      }).catch(() => toast.error("Projet introuvable")).finally(() => setLoading(false));
    }
  }, [projectId]);

  // No project yet: show entry point
  if (!projectId) {
    return (
      <div className="pt-[100px] md:pt-[140px] pb-24">
        <Seo
          title="Estimation de travaux en ligne — Prix de votre rénovation en 5 minutes | RAMA DÉCO"
          description="Décrivez vos travaux avec vos propres mots et obtenez une estimation de budget ventilée par poste en 5 minutes. Gratuit, sans engagement, partout en Île-de-France."
          path="/estimation"
          jsonLd={[localBusinessLd(), breadcrumbLd([{ to: "/", label: "Accueil" }, { to: "/estimation", label: "Estimation" }])]}
        />
        <div className="wrap">
          <div className="font-mono-caption text-bordeaux mb-4">Estimation</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight text-stone-900 max-w-3xl">Comment souhaitez-vous <span className="italic text-bordeaux">commencer ?</span></h1>

          <div className="mt-14 grid md:grid-cols-2 gap-8">
            <div className="p-8 md:p-10 bg-white border-2 border-bordeaux rounded-sm relative">
              <div className="absolute -top-3 left-8 bg-bordeaux text-stone-50 px-3 py-1 text-xs font-mono-caption">Recommandé</div>
              <div className="font-mono-caption text-bordeaux mb-3">Option 1</div>
              <div className="font-display text-3xl text-stone-900">Décrire mon projet</div>
              <p className="mt-4 text-stone-600 leading-relaxed">Expliquez simplement ce que vous souhaitez réaliser, comme si vous parliez directement à un professionnel RAMA DÉCO.</p>
              <div className="mt-8"><HeroPromptInput compact /></div>
            </div>

            <div className="p-8 md:p-10 bg-white border border-stone-300 rounded-sm">
              <div className="font-mono-caption text-stone-500 mb-3">Option 2</div>
              <div className="font-display text-3xl text-stone-900">Choisir mes travaux</div>
              <p className="mt-4 text-stone-600 leading-relaxed">Vous savez déjà précisément ce qu'il vous faut ? Sélectionnez directement les travaux concernés dans notre catalogue.</p>
              <Link to="/catalogue" data-testid="option-catalogue" className="btn-ghost mt-8">Choisir mes travaux <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !project) return <div className="pt-40 wrap"><div className="font-mono-caption text-stone-500">Chargement…</div></div>;
  if (!project) return null;

  const ai = project.ai_analysis || {};

  return (
    <div className="pt-[100px] md:pt-[140px] pb-24">
      <Seo title={`Projet ${project.ref} — Estimation | RAMA DÉCO`} description="Estimation détaillée de votre projet." path={`/estimation/${project.id}`} noindex />
      <div className="wrap">
        <div className="font-mono-caption text-bordeaux mb-3">Projet {project.ref}</div>
        <h1 className="font-display text-3xl md:text-5xl tracking-tight text-stone-900">Voici ce que nous avons <span className="italic text-bordeaux">compris.</span></h1>

        {ai.resume_humain && (
          <div className="mt-8 p-6 md:p-8 bg-white border-l-4 border-bordeaux-700 rounded-rama-md max-w-3xl">
            <div className="font-mono-caption text-ink-soft mb-3 flex items-center gap-2"><Sparkles size={14} /> Résumé</div>
            <p className="text-anthracite text-lg leading-relaxed">{ai.resume_humain}</p>
          </div>
        )}

        {project.out_of_scope_items && project.out_of_scope_items.length > 0 && (
          <div className="mt-4 p-6 bg-greige/40 border-l-4 border-anthracite rounded-rama-md max-w-3xl">
            <div className="font-mono-caption text-anthracite mb-2">Hors périmètre RAMA DÉCO</div>
            <p className="text-sm text-ink-soft leading-relaxed mb-3">
              RAMA DÉCO intervient sur les travaux intérieurs et le second œuvre.
              Les éléments ci-dessous concernent la structure du bâtiment et nécessitent une entreprise spécialisée en gros œuvre :
            </p>
            <ul className="text-sm text-anthracite space-y-1 list-disc pl-5">
              {project.out_of_scope_items.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
            <p className="text-xs text-ink-soft mt-3">Nous pouvons toutefois étudier les travaux intérieurs qui suivront.</p>
          </div>
        )}

        <StudyPanel project={project} onVisit={() => nav(`/rdv/${project.id}`)} />
      </div>
    </div>
  );
}
